import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { OptionsIcon } from '../TipSheetIcons/options-icon';
import { ChevronDownIcon } from '../TipSheetIcons/chevron-down';
import { SortIcon } from '../TipSheetIcons/sort-icon';
import { ClusterIcon } from '../TipSheetIcons/cluster-icon';

const OptionsWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: ${(props) => (props.$isMobile ? '100%' : 'auto')};

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const OptionsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 0.25rem;
  padding: 0 0.375rem 0 0.75rem;
  height: 2rem;
  background-color: #ffffff;
  border: 1px solid #cccccc;
  font-size: 0.875rem;
  cursor: pointer;
  width: ${(props) => (props.$isMobile ? '100%' : '9.375rem')};
  color: #212121;
`;

const OptionsDropdown = styled.div`
  position: ${(props) => (props.$isMobile ? 'static' : 'absolute')};
  z-index: ${(props) => (props.$isMobile ? '1' : '2')};
  top: ${(props) => (props.$isMobile ? 'auto' : '2.5rem')};
  right: ${(props) => (props.$isMobile ? 'auto' : '0')};
  background-color: ${(props) => (props.$isMobile ? 'transparent' : '#ffffff')};
  border-radius: ${(props) => (props.$isMobile ? '0' : '0.25rem')};
  box-shadow: ${(props) =>
    props.$isMobile ? 'none' : '0px 14px 64px -20px #000d512b'};
  border: ${(props) => (props.$isMobile ? 'none' : '1px solid #cccccc')};
  padding: ${(props) => (props.$isMobile ? '0' : '0.75rem')};
  width: ${(props) => (props.$isMobile ? '100%' : '12rem')};
`;

const OptionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &:not(:last-child) {
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 0.75rem;
    margin-bottom: 0.75rem;
  }
`;

const OptionGroupTitle = styled.div`
  font-weight: 600;
  color: #212121;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0.25rem 0;
  font-weight: 600;
  position: relative;

  /* Hide the default radio button */
  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
  }

  /* Custom radio button appearance */
  span.radio-control {
    display: inline-block;
    width: 20px;
    height: 20px;
    margin-right: 0.5rem;
    border-radius: 50%;
    border: 3px solid #cccccc;
    background-color: #ffffff;
    position: relative;
  }

  /* Create inner circle for unchecked state */
  span.radio-control::after {
    content: '';
    position: absolute;
    display: block;
    top: 50%;
    left: 50%;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: transparent;
    transition: all 0.2s ease;
    transform: translate(-50%, -50%);
  }

  /* Active state styling */
  input:checked + span.radio-control {
    border: 3px solid #0a819f;
  }

  /* Active inner circle styling */
  input:checked + span.radio-control::after {
    background: #0a819f;
    border: 2px solid #ffffff;
  }

  /* Focus state for accessibility */
  input:focus + span.radio-control {
    outline: 2px solid #0a819f;
    outline-offset: 2px;
  }

  /* Hover effect */
  &:hover span.radio-control {
    border-color: #0a819f;
  }

  &:hover {
    color: #0a819f;
  }
`;

const SVGWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  @media (max-width: 768px) {
    gap: 0.25rem;
  }
`;

const ChevronUpIcon = styled(ChevronDownIcon)`
  transform: rotate(180deg);
`;

const ToolbarPair = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-wrap: nowrap;
    width: 100%;
    padding-top: 1rem;
    border-top: 1px solid #e0e0e0;
  }
`;

const ToolbarControl = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.375rem;

  @media (max-width: 768px) {
    min-width: 0;
    justify-content: center;
  }
`;

const ToolbarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.375rem;
  border-radius: 0.25rem;
  padding: 0 0.5rem 0 0.5rem;
  height: 2rem;
  min-width: 9rem;
  background-color: #ffffff;
  border: 1px solid #cccccc;
  font-size: 0.875rem;
  cursor: pointer;
  color: #212121;

  @media (max-width: 768px) {
    min-width: 0;
    flex: 1;
    width: auto;
    font-size: 0.75rem;
    padding: 0 0.25rem;
    gap: 0.25rem;
  }
`;

const ToolbarDropdown = styled.div`
  position: absolute;
  z-index: 3;
  top: calc(100% + 4px);
  right: 0;
  background-color: #ffffff;
  border-radius: 0.25rem;
  box-shadow: 0px 14px 64px -20px #000d512b;
  border: 1px solid #cccccc;
  padding: 0.75rem;
  width: 12rem;
`;

function sortDisplayLabel(sortBy, searchActive) {
  if (searchActive) return 'Relevance';
  if (sortBy === 'chrono') return 'Date';
  return 'Score';
}

function clusterDisplayLabel(clusterBy) {
  switch (clusterBy) {
    case 'committee':
      return 'Committee';
    case 'bill':
      return 'Bill';
    case 'legislator':
      return 'Legislator';
    case 'topic':
      return 'Topic';
    case 'none':
    default:
      return 'None';
  }
}

export default function FilterOptions({
  sortBy,
  setSortBy,
  clusterBy,
  setClusterBy,
  searchActive,
  isMobile = false,
  variant = 'default',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [clusterMenuOpen, setClusterMenuOpen] = useState(false);
  const optionsRef = useRef(null);
  const toolbarSortRef = useRef(null);
  const toolbarClusterRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (optionsRef.current && !optionsRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        toolbarSortRef.current &&
        !toolbarSortRef.current.contains(event.target)
      ) {
        setSortMenuOpen(false);
      }
      if (
        toolbarClusterRef.current &&
        !toolbarClusterRef.current.contains(event.target)
      ) {
        setClusterMenuOpen(false);
      }
    }

    if (sortMenuOpen || clusterMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sortMenuOpen, clusterMenuOpen]);

  // The options content that appears in the dropdown
  const OptionsContent = () => (
    <>
      <OptionGroup>
        <OptionGroupTitle>
          <SVGWrapper>
            <ClusterIcon />
            Cluster By
          </SVGWrapper>
        </OptionGroupTitle>
        <RadioOption>
          <input
            type="radio"
            id={`committee${isMobile ? '-mobile' : ''}`}
            name={`clusterBy${isMobile ? '-mobile' : ''}`}
            checked={clusterBy === 'committee'}
            onChange={() => setClusterBy('committee')}
          />
          <span className="radio-control"></span>
          <label htmlFor={`committee${isMobile ? '-mobile' : ''}`}>
            Committee
          </label>
        </RadioOption>
        <RadioOption>
          <input
            type="radio"
            id={`bill${isMobile ? '-mobile' : ''}`}
            name={`clusterBy${isMobile ? '-mobile' : ''}`}
            checked={clusterBy === 'bill'}
            onChange={() => setClusterBy('bill')}
          />
          <span className="radio-control"></span>
          <label htmlFor={`bill${isMobile ? '-mobile' : ''}`}>Bill</label>
        </RadioOption>
        <RadioOption>
          <input
            type="radio"
            id={`legislator${isMobile ? '-mobile' : ''}`}
            name={`clusterBy${isMobile ? '-mobile' : ''}`}
            checked={clusterBy === 'legislator'}
            onChange={() => setClusterBy('legislator')}
          />
          <span className="radio-control"></span>
          <label htmlFor={`legislator${isMobile ? '-mobile' : ''}`}>
            Legislator
          </label>
        </RadioOption>
        <RadioOption>
          <input
            type="radio"
            id={`topic${isMobile ? '-mobile' : ''}`}
            name={`clusterBy${isMobile ? '-mobile' : ''}`}
            checked={clusterBy === 'topic'}
            onChange={() => setClusterBy('topic')}
          />
          <span className="radio-control"></span>
          <label htmlFor={`topic${isMobile ? '-mobile' : ''}`}>Topic</label>
        </RadioOption>
        <RadioOption>
          <input
            type="radio"
            id={`none${isMobile ? '-mobile' : ''}`}
            name={`clusterBy${isMobile ? '-mobile' : ''}`}
            checked={clusterBy === 'none'}
            onChange={() => setClusterBy('none')}
          />
          <span className="radio-control"></span>
          <label htmlFor={`none${isMobile ? '-mobile' : ''}`}>None</label>
        </RadioOption>
      </OptionGroup>
      <OptionGroup>
        <OptionGroupTitle>
          <SVGWrapper>
            <SortIcon />
            Sort By
          </SVGWrapper>
        </OptionGroupTitle>
        {searchActive ? (
          <RadioOption>
            <input
              type="radio"
              id={`relevance${isMobile ? '-mobile' : ''}`}
              name={`sortBy${isMobile ? '-mobile' : ''}`}
              checked={true}
              readOnly
            />
            <span className="radio-control"></span>
            <label htmlFor={`relevance${isMobile ? '-mobile' : ''}`}>
              Relevance
            </label>
          </RadioOption>
        ) : (
          <>
            <RadioOption>
              <input
                type="radio"
                id={`score${isMobile ? '-mobile' : ''}`}
                name={`sortBy${isMobile ? '-mobile' : ''}`}
                checked={sortBy === 'score'}
                onChange={() => setSortBy('score')}
              />
              <span className="radio-control"></span>
              <label htmlFor={`score${isMobile ? '-mobile' : ''}`}>Score</label>
            </RadioOption>
            <RadioOption>
              <input
                type="radio"
                id={`chrono${isMobile ? '-mobile' : ''}`}
                name={`sortBy${isMobile ? '-mobile' : ''}`}
                checked={sortBy === 'chrono'}
                onChange={() => setSortBy('chrono')}
              />
              <span className="radio-control"></span>
              <label htmlFor={`chrono${isMobile ? '-mobile' : ''}`}>Date</label>
            </RadioOption>
          </>
        )}
      </OptionGroup>
    </>
  );

  if (variant === 'toolbar') {
    return (
      <ToolbarPair>
        <ToolbarControl ref={toolbarSortRef}>
          <div style={{ position: 'relative' }}>
            <ToolbarButton
              type="button"
              onClick={() => {
                setClusterMenuOpen(false);
                setSortMenuOpen((o) => !o);
              }}
              aria-expanded={sortMenuOpen}
            >
              <SVGWrapper>
                <SortIcon />
                Sort by: <strong>{sortDisplayLabel(sortBy, searchActive)}</strong>
              </SVGWrapper>
              {sortMenuOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </ToolbarButton>
            {sortMenuOpen && (
              <ToolbarDropdown>
                <OptionGroup>
                  {searchActive ? (
                    <RadioOption>
                      <input
                        type="radio"
                        id="toolbar-relevance"
                        name="sortBy-toolbar"
                        checked={true}
                        readOnly
                      />
                      <span className="radio-control"></span>
                      <label htmlFor="toolbar-relevance">Relevance</label>
                    </RadioOption>
                  ) : (
                    <>
                      <RadioOption>
                        <input
                          type="radio"
                          id="toolbar-score"
                          name="sortBy-toolbar"
                          checked={sortBy === 'score'}
                          onChange={() => {
                            setSortBy('score');
                            setSortMenuOpen(false);
                          }}
                        />
                        <span className="radio-control"></span>
                        <label htmlFor="toolbar-score">Score</label>
                      </RadioOption>
                      <RadioOption>
                        <input
                          type="radio"
                          id="toolbar-chrono"
                          name="sortBy-toolbar"
                          checked={sortBy === 'chrono'}
                          onChange={() => {
                            setSortBy('chrono');
                            setSortMenuOpen(false);
                          }}
                        />
                        <span className="radio-control"></span>
                        <label htmlFor="toolbar-chrono">Date</label>
                      </RadioOption>
                    </>
                  )}
                </OptionGroup>
              </ToolbarDropdown>
            )}
          </div>
        </ToolbarControl>

        <ToolbarControl ref={toolbarClusterRef}>
          <div style={{ position: 'relative' }}>
            <ToolbarButton
              type="button"
              onClick={() => {
                setSortMenuOpen(false);
                setClusterMenuOpen((o) => !o);
              }}
              aria-expanded={clusterMenuOpen}
            >
              <SVGWrapper>
                <ClusterIcon />
                Cluster by: <strong>{clusterDisplayLabel(clusterBy)}</strong>
              </SVGWrapper>
              {clusterMenuOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </ToolbarButton>
            {clusterMenuOpen && (
              <ToolbarDropdown>
                <OptionGroup>
                  <RadioOption>
                    <input
                      type="radio"
                      id="toolbar-cluster-committee"
                      name="clusterBy-toolbar"
                      checked={clusterBy === 'committee'}
                      onChange={() => {
                        setClusterBy('committee');
                        setClusterMenuOpen(false);
                      }}
                    />
                    <span className="radio-control"></span>
                    <label htmlFor="toolbar-cluster-committee">Committee</label>
                  </RadioOption>
                  <RadioOption>
                    <input
                      type="radio"
                      id="toolbar-cluster-bill"
                      name="clusterBy-toolbar"
                      checked={clusterBy === 'bill'}
                      onChange={() => {
                        setClusterBy('bill');
                        setClusterMenuOpen(false);
                      }}
                    />
                    <span className="radio-control"></span>
                    <label htmlFor="toolbar-cluster-bill">Bill</label>
                  </RadioOption>
                  <RadioOption>
                    <input
                      type="radio"
                      id="toolbar-cluster-legislator"
                      name="clusterBy-toolbar"
                      checked={clusterBy === 'legislator'}
                      onChange={() => {
                        setClusterBy('legislator');
                        setClusterMenuOpen(false);
                      }}
                    />
                    <span className="radio-control"></span>
                    <label htmlFor="toolbar-cluster-legislator">
                      Legislator
                    </label>
                  </RadioOption>
                  <RadioOption>
                    <input
                      type="radio"
                      id="toolbar-cluster-topic"
                      name="clusterBy-toolbar"
                      checked={clusterBy === 'topic'}
                      onChange={() => {
                        setClusterBy('topic');
                        setClusterMenuOpen(false);
                      }}
                    />
                    <span className="radio-control"></span>
                    <label htmlFor="toolbar-cluster-topic">Topic</label>
                  </RadioOption>
                  <RadioOption>
                    <input
                      type="radio"
                      id="toolbar-cluster-none"
                      name="clusterBy-toolbar"
                      checked={clusterBy === 'none'}
                      onChange={() => {
                        setClusterBy('none');
                        setClusterMenuOpen(false);
                      }}
                    />
                    <span className="radio-control"></span>
                    <label htmlFor="toolbar-cluster-none">None</label>
                  </RadioOption>
                </OptionGroup>
              </ToolbarDropdown>
            )}
          </div>
        </ToolbarControl>
      </ToolbarPair>
    );
  }

  return (
    <OptionsWrapper ref={optionsRef} $isMobile={isMobile}>
      {/* Only show the button in desktop mode */}
      {!isMobile && (
        <OptionsButton onClick={() => setIsOpen(!isOpen)} $isMobile={isMobile}>
          <SVGWrapper>
            <OptionsIcon />
            Sort/Cluster
          </SVGWrapper>
          {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </OptionsButton>
      )}

      {/* In mobile mode, always show the content without a dropdown */}
      {/* In desktop mode, only show when dropdown is open */}
      {(isMobile || isOpen) && (
        <OptionsDropdown $isMobile={isMobile}>
          <OptionsContent />
        </OptionsDropdown>
      )}
    </OptionsWrapper>
  );
}
